import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Shared copy-to-clipboard chain for prompt copy buttons.
 *
 * Chain: navigator.clipboard (secure context) → document.execCommand('copy')
 * via hidden textarea → on total failure, `copyFailed` flips true so the
 * caller can switch to a "press ⌘C" affordance and select the source text.
 *
 * `copied` flashes true for 1400ms after a successful write. Rapid successive
 * copies reset the timer so the flash always lasts 1400ms from the last copy.
 * `copied` is never set unless the write actually succeeded.
 */
export function useCopyPrompt() {
  const [copied, setCopied] = useState(false)
  const [copyFailed, setCopyFailed] = useState(false)
  const timeoutRef = useRef<number | null>(null)

  useEffect(() => {
    return () => {
      if (timeoutRef.current !== null) window.clearTimeout(timeoutRef.current)
    }
  }, [])

  const flashCopied = useCallback(() => {
    setCopyFailed(false)
    setCopied(true)
    if (timeoutRef.current !== null) window.clearTimeout(timeoutRef.current)
    timeoutRef.current = window.setTimeout(() => {
      setCopied(false)
      timeoutRef.current = null
    }, 1400)
  }, [])

  const fallbackCopy = useCallback((text: string) => {
    const textArea = document.createElement('textarea')
    textArea.value = text
    textArea.style.position = 'fixed'
    textArea.style.opacity = '0'
    document.body.appendChild(textArea)
    textArea.select()

    let succeeded = false
    try {
      succeeded = document.execCommand('copy')
    } catch {
      succeeded = false
    } finally {
      document.body.removeChild(textArea)
    }

    if (succeeded) {
      flashCopied()
    } else {
      setCopyFailed(true)
    }
  }, [flashCopied])

  const copy = useCallback((text: string) => {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(flashCopied).catch(() => fallbackCopy(text))
      return
    }

    fallbackCopy(text)
  }, [fallbackCopy, flashCopied])

  return { copied, copyFailed, copy }
}
